import React, {useEffect} from 'react';
import {motion} from 'framer-motion';
import {FaFacebook, FaInstagram, FaThreads} from 'react-icons/fa6';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContentDate from '../components/ContentDate';
import {useLanguage} from '../contexts/LanguageContext';

const {FiExternalLink, FiHeart, FiMusic, FiUsers} = FiIcons;

const About = () => {
  const {language} = useLanguage();
  const isChinese = language === 'zh-TW';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = isChinese ? {
    title: '關於 Beat Master',
    intro: '認識這款免費線上拍子機背後的團隊、設計目標，以及與我們聯絡的方法。',
    who: '誰開發了 Beat Master？',
    whoText: 'Beat Master 由 Homy Tech Limited 開發。我們專注製作實用、易用且適合不同裝置的數碼工具。',
    purpose: 'Beat Master 為什麼存在？',
    purposeText: '我們希望讓學生、音樂導師、樂手和表演者都能免費使用精準、容易操作的線上拍子機。',
    contact: '如何與我們聯絡？',
    contactText: '您可以透過 Homy Tech Limited 官方網站或以下社交平台與團隊聯絡。',
    website: '瀏覽 Homy Tech Limited 網站',
    updated: '2025-08-19',
  } : {
    title: 'About Beat Master',
    intro: 'Meet the team behind this free online metronome, learn why it exists, and find out how to contact us.',
    who: 'Who is behind Beat Master?',
    whoText: 'Beat Master is developed by Homy Tech Limited, a team focused on creating practical, accessible digital tools that work across devices.',
    purpose: 'Why was Beat Master created?',
    purposeText: 'We built Beat Master to give students, educators, musicians and performers a free, precise and approachable online metronome.',
    contact: 'How can you get in touch?',
    contactText: 'Contact the team through the Homy Tech Limited website or connect with us using one of the official social channels below.',
    website: 'Visit Homy Tech Limited',
    updated: '2025-08-19',
  };

  const socials = [
    {href: 'https://www.threads.com/@homy.hk', label: 'Threads', icon: FaThreads},
    {href: 'https://www.instagram.com/homy.hk', label: 'Instagram', icon: FaInstagram},
    {
      href: 'https://www.facebook.com/profile.php?id=61578745554661',
      label: 'Facebook',
      icon: FaFacebook,
    },
  ];

  const sections = [
    {title: content.who, text: content.whoText, icon: FiUsers},
    {title: content.purpose, text: content.purposeText, icon: FiMusic},
  ];

  return (
    <article className="max-w-4xl mx-auto px-4">
      <motion.header
        className="text-center mb-12"
        initial={{opacity: 0, y: -20}}
        animate={{opacity: 1, y: 0}}
      >
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {content.title}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {content.intro}
        </p>
        <ContentDate
          published="2025-08-19"
          updated={content.updated}
          language={language}
          className="mt-5"
        />
      </motion.header>

      <div className="grid gap-6 md:grid-cols-2">
        {sections.map((section, index) => (
          <motion.section
            key={section.title}
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-7 shadow-sm"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: index * 0.12}}
          >
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-5">
              <SafeIcon icon={section.icon} className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {section.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              {section.text}
            </p>
          </motion.section>
        ))}
      </div>

      <motion.section
        className="mt-8 rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-100 dark:border-purple-800/40 p-8 text-center"
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.25}}
      >
        <SafeIcon icon={FiHeart} className="w-8 h-8 text-pink-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {content.contact}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-6">
          {content.contactText}
        </p>
        <a
          href="https://homy.hk"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium"
        >
          {content.website}
          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
        </a>
        <div className="flex justify-center gap-3 mt-6">
          {socials.map(({href, label, icon}) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className="p-3 rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm hover:text-purple-600 dark:hover:text-purple-400"
            >
              <SafeIcon icon={icon} className="w-5 h-5" />
            </a>
          ))}
        </div>
      </motion.section>
    </article>
  );
};

export default About;